import React, { forwardRef, useCallback } from 'react';
import { Link, type LinkProps } from 'react-router';
import type { HTMLAttributes } from 'react';
import { useLegacyAppLink } from '../../contexts/LegacyAppLinkContext';

// Base props that are common to all modes
interface BaseOptionalLinkProps extends HTMLAttributes<HTMLAnchorElement> {
    children: React.ReactNode;
    className?: string;
    prefetch?: LinkProps['prefetch'];
    viewTransition?: LinkProps['viewTransition'];
    isNonRemixRoute?: boolean;
    newTab?: boolean;
    state?: unknown;
}

// Discriminated union for type-safe mode-based props
type OptionalLinkProps = BaseOptionalLinkProps &
    (
        | {
              mode?: 'normal';
              to: string;
          }
        | {
              mode: 'override';
              to?: string; // Optional for override mode - used as href for right-click support
          }
        | {
              mode: 'disable';
              to?: string;
          }
    );

/**
 * OptionalLink component that provides flexible link rendering with three distinct modes.
 *
 * This component is designed to handle different navigation scenarios in a type-safe manner:
 * - **normal**: Renders a standard link with full navigation capabilities using Remix Link or regular anchor tags
 * - **override**: Renders an anchor tag with custom onClick handler while preserving right-click functionality,
 *   the normal link on-click behaviour is overriden
 * - **disable**: Renders children without any link wrapper, useful for conditional linking
 *
 * @example
 * ```tsx
 * // Normal mode - standard navigation
 * <OptionalLink to="/products" mode="normal">
 *   View Products
 * </OptionalLink>
 *
 * // Override mode - custom click handling with right-click support, the normal
 * // link on-click behaviour is overidden
 * <OptionalLink to="/products" mode="override" onClick={handleCustomAction}>
 *   Custom Action
 * </OptionalLink>
 *
 * // Disable mode - no link functionality
 * <OptionalLink mode="disable">
 *   Disabled Content
 * </OptionalLink>
 * ```
 *
 * @param props - The component props
 * @param props.mode - The rendering mode ('normal' | 'override' | 'disable')
 * @param props.to - The destination URL (required for 'normal' mode, optional for 'override')
 * @param props.onClick - Click handler function (required for 'normal' and 'override' modes)
 * @param props.children - The content to render inside the link
 * @param props.className - CSS classes to apply to the link element
 * @param props.prefetch - Remix prefetch behavior for normal mode
 * @param props.isNonRemixRoute - Whether to use regular anchor instead of Remix Link
 * @param props.newTab - Whether to open the link in a new tab
 * @param props.state - State to pass to Remix navigation
 * @returns A link component or fragment based on the mode
 */
export const OptionalLink = React.memo(
    forwardRef<HTMLAnchorElement, OptionalLinkProps>((props, ref) => {
        const {
            children,
            mode = 'normal',
            to,
            onClick,
            className,
            prefetch,
            viewTransition,
            isNonRemixRoute = false,
            newTab,
            state,
            ...restProps
        } = props;

        const useLegacyLink = useLegacyAppLink();

        // Memoized click handler for override mode
        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLAnchorElement>) => {
                // Prevent default navigation in override mode
                if (mode === 'override') {
                    event.preventDefault();
                }
                onClick?.(event);
            },
            [mode, onClick],
        );

        // Disable mode - render children as fragment
        if (mode === 'disable') {
            return <>{children}</>;
        }

        // Override mode - render anchor with onClick
        if (mode === 'override') {
            return (
                <a
                    ref={ref}
                    href={to || '#'} // Use provided to prop or fallback to #
                    className={className}
                    target={newTab ? '_blank' : undefined}
                    rel={newTab ? 'noreferrer' : undefined}
                    role="button"
                    tabIndex={0}
                    onClick={handleClick}
                    {...restProps}
                >
                    {children}
                </a>
            );
        }

        // Normal mode - render link with navigation
        const linkProps = {
            className,
            target: newTab ? '_blank' : undefined,
            rel: newTab ? 'noreferrer' : undefined,
            onClick: handleClick,
            ...restProps,
        };

        // Use legacy link or regular anchor for non-Remix routes
        if (useLegacyLink || isNonRemixRoute) {
            return (
                <a ref={ref} href={to} {...linkProps}>
                    {children}
                </a>
            );
        }

        // Use Remix Link for internal routes
        return (
            <Link
                ref={ref}
                to={to!} // Safe to use ! here since we're in normal mode where to is required
                prefetch={prefetch}
                viewTransition={viewTransition}
                state={state}
                {...linkProps}
            >
                {children}
            </Link>
        );
    }),
);

OptionalLink.displayName = 'OptionalLink';
